import React, { useState, ChangeEvent, FormEvent } from 'react';

interface FileUploadFormProps {
  onSubmit: (file: File, outputType: 'json' | 'markdown', options: { pretty?: boolean, title?: string }) => void;
  isLoading: boolean;
}

export const FileUploadForm: React.FC<FileUploadFormProps> = ({ onSubmit, isLoading }) => {
  const [file, setFile] = useState<File | null>(null);
  const [outputType, setOutputType] = useState<'json' | 'markdown'>('json');
  const [prettyPrint, setPrettyPrint] = useState<boolean>(true);
  const [markdownTitle, setMarkdownTitle] = useState<string>('');

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (file) {
      const options = outputType === 'json'
          ? { pretty: prettyPrint }
          : { title: markdownTitle || undefined };

      onSubmit(file, outputType, options);
    }
  };

  return (
      <form onSubmit={handleSubmit} className="bg-white p-4 rounded-lg border border-gray-200">
        <h2 className="text-lg font-medium mb-4 text-center">Upload Excel File</h2>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Excel File (.xlsx)
          </label>
          <input
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              required
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Output Format
          </label>
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                  type="radio"
                  className="form-radio"
                  value="json"
                  checked={outputType === 'json'}
                  onChange={() => setOutputType('json')}
              />
              <span className="ml-2">JSON</span>
            </label>
            <label className="inline-flex items-center">
              <input
                  type="radio"
                  className="form-radio"
                  value="markdown"
                  checked={outputType === 'markdown'}
                  onChange={() => setOutputType('markdown')}
              />
              <span className="ml-2">Markdown</span>
            </label>
          </div>
        </div>

        {outputType === 'json' && (
            <div className="mb-4">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <input
                    type="checkbox"
                    checked={prettyPrint}
                    onChange={(e) => setPrettyPrint(e.target.checked)}
                    className="mr-2"
                />
                Pretty Print JSON
              </label>
            </div>
        )}

        {outputType === 'markdown' && (
            <div className="mb-4">
              <label htmlFor="markdown-title" className="block text-sm font-medium text-gray-700 mb-1">
                Markdown Title (Optional)
              </label>
              <input
                  id="markdown-title"
                  type="text"
                  value={markdownTitle}
                  onChange={(e) => setMarkdownTitle(e.target.value)}
                  placeholder="Enter a title for the markdown document"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
        )}

        <button
            type="submit"
            disabled={!file || isLoading}
            className="tcmf-button w-full"
        >
          {isLoading ? 'Processing...' : 'Generate TCMF'}
        </button>
      </form>
  );
};